import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack,
  Launch,
  TrendingUp,
  AccessTime,
  Star,
  SwapHoriz,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { LoyaltyProgram, Transaction } from '../types';
import { TransactionItem } from '../components/common/TransactionItem';
import { realDataService } from '../services/realDataService';
import { useAuth } from '../contexts/AuthContext';
import { getTierColor, getProgramTypeColor } from '../theme';

const HeroCard = styled(Card)<{ programType: string }>(({ theme, programType }) => ({
  background: `linear-gradient(135deg, ${getProgramTypeColor(programType as any)} 0%, ${getProgramTypeColor(programType as any)}80 100%)`,
  color: 'white',
  marginBottom: theme.spacing(3),
}));

const StatsCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  height: '100%',
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`program-tabpanel-${index}`}
      aria-labelledby={`program-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProgramDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [programsData, transactionsData] = await Promise.all([
          realDataService.getLoyaltyPrograms(user?.id),
          realDataService.getTransactions(user?.id)
        ]);
        setPrograms(programsData);
        setTransactions(transactionsData);
      } catch (error) {
        console.error('Error fetching program data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const program = programs.find((p: LoyaltyProgram) => p.id === id);
  const programTransactions = transactions.filter((t: Transaction) => t.programId === id);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Loading program details...</Typography>
        <LinearProgress />
      </Container>
    );
  }

  if (!program) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4">Program not found</Typography>
      </Container>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatPoints = (points: number): string => {
    if (points >= 1000000) return `${(points / 1000000).toFixed(1)}M`;
    if (points >= 1000) return `${(points / 1000).toFixed(1)}K`;
    return points.toLocaleString();
  };

  const getExpiryProgress = (): number => {
    if (!program.expiryDate) return 0;
    const now = new Date();
    const expiry = new Date(program.expiryDate);
    const total = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
    const remaining = expiry.getTime() - now.getTime();
    return Math.max(0, Math.min(100, ((total - remaining) / total) * 100));
  };

  const getNextTierInfo = () => {
    const tiers = {
      'airline': ['Basic', 'Silver', 'Gold', 'Platinum', 'Diamond'],
      'hotel': ['Member', 'Silver', 'Gold', 'Platinum', 'Ambassador'],
      'credit_card': ['Standard', 'Premium', 'Elite', 'Black']
    };
    
    const currentTiers = tiers[program.type] || tiers.credit_card;
    const currentIndex = currentTiers.findIndex(tier => 
      program.tier.toLowerCase().includes(tier.toLowerCase())
    );
    
    if (currentIndex < currentTiers.length - 1) {
      return {
        nextTier: currentTiers[currentIndex + 1],
        progress: 65, // Mock progress
        pointsNeeded: 25000, // Mock points needed
      };
    }
    return null;
  };

  const nextTierInfo = getNextTierInfo();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          {program.name}
        </Typography>
        <Button
          variant="outlined"
          endIcon={<Launch />}
          onClick={() => window.open(program.website, '_blank')}
        >
          Visit Website
        </Button>
      </Box>

      {/* Hero Section */}
      <HeroCard programType={program.type}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={program.logo} alt={program.name} style={{ width: 60, height: 60, objectFit: 'contain' }} />
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatPoints(program.points)}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {program.currency}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  label={program.tier}
                  sx={{
                    backgroundColor: getTierColor(program.tier),
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={program.type.replace('_', ' ').toUpperCase()}
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.5)' }}
                />
              </Box>

              {program.expiryDate && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    Points expire on {format(new Date(program.expiryDate), 'MMMM dd, yyyy')}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={getExpiryProgress()} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getExpiryProgress() > 75 ? '#F56565' : '#48BB78',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              {nextTierInfo && (
                <StatsCard>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      Next Tier
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: getTierColor(nextTierInfo.nextTier) }}>
                      {nextTierInfo.nextTier}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {nextTierInfo.pointsNeeded.toLocaleString()} points needed
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={nextTierInfo.progress} 
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </CardContent>
                </StatsCard>
              )}

              <StatsCard>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    Estimated Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#38A169' }}>
                    ${Math.round(program.points * (program.conversionRate || 1) * 0.01).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {((program.conversionRate || 1) * 0.01).toFixed(2)}¢ per point
                  </Typography>
                </CardContent>
              </StatsCard>
            </Box>
          </Box>
        </CardContent>
      </HeroCard>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Transactions" />
            <Tab label="Redemptions" />
            <Tab label="Transfers" />
            <Tab label="Analytics" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Transaction History
          </Typography>
          
          {programTransactions.length > 0 ? (
            <Box>
              {programTransactions.map((transaction) => (
                <TransactionItem key={transaction.id} transaction={transaction} />
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">
              No transactions found for this program.
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Available Redemptions
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 2 }}>
            {/* Mock redemption options */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Round-trip Flight
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Domestic flights within the US
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      25,000 {program.currency} • 1.2¢/point
                    </Typography>
                  </Box>
                  <Button variant="outlined" endIcon={<Launch />}>
                    Redeem
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Hotel Stay
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Premium hotel properties worldwide
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      40,000 {program.currency} • 0.8¢/point
                    </Typography>
                  </Box>
                  <Button variant="outlined" endIcon={<Launch />}>
                    Redeem
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Transfer Options
          </Typography>
          
          <Typography color="textSecondary">
            Transfer partners and conversion rates will be displayed here.
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Points Analytics
          </Typography>
          
          <Typography color="textSecondary">
            Charts and analytics about your points usage will be displayed here.
          </Typography>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default ProgramDetails;